import clsx from 'clsx';
import { PropertyMap, type PropertyMapProps } from './PropertyMap';
import { SnapshotBox, type SnapshotEntry } from './SnapshotBox';
import { UnitMixBar, type UnitMixRow } from './UnitMixBar';

export interface PropertyVisualGridProps {
  /**
   * Property map slot — replaces the Phase-6 `<SatelliteImage>` placeholder
   * at the same mount point (`external-sharing` Phase 6 Session 6.3).
   * The `lat`/`lng` keys are sourced from `agent_state.aerialLatLng` once
   * Session 6.4's geocoding hook lands; until then, callers continue to
   * pass `null` and the map renders the no-coordinates empty state.
   */
  satellite: Pick<PropertyMapProps, 'lat' | 'lng' | 'zoom'>;
  snapshotEntries: SnapshotEntry[];
  unitMixRows: UnitMixRow[];
  className?: string;
}

/**
 * 3-column grid wrapping the Property Visual Context row's three primitives.
 * On narrower viewports the grid collapses to 2 columns then 1 column —
 * the satellite image stays full-width on mobile so it remains readable.
 *
 * Pure layout — all data fetching + value formatting is the parent variant
 * component's responsibility. Keeping this component thin lets the
 * Operating-Asset and New-Development variants pass different snapshot
 * field sets through the same wrapper without conditionals.
 */
export function PropertyVisualGrid({
  satellite,
  snapshotEntries,
  unitMixRows,
  className,
}: PropertyVisualGridProps) {
  return (
    <div
      data-testid="property-visual-grid"
      className={clsx(
        'grid h-[170px] px-8 py-2 gap-2',
        className,
      )}
      style={{ gridTemplateColumns: 'minmax(0,0.4fr) minmax(0,0.35fr) minmax(0,0.25fr)' }}
    >
      <div className="border border-panel-border overflow-hidden min-w-0 min-h-0">
        <PropertyMap {...satellite} />
      </div>
      <div className="border border-panel-border overflow-hidden min-w-0 min-h-0">
        <SnapshotBox entries={snapshotEntries} bare />
      </div>
      <div className="flex flex-col border border-panel-border overflow-hidden min-w-0 min-h-0 p-2.5 gap-1.5">
        <span className="text-[7px] font-mono text-text-muted uppercase tracking-[1.1px] font-semibold">
          Unit Mix
        </span>
        <UnitMixBar rows={unitMixRows} compact />
      </div>
    </div>
  );
}
